"use client";

import { Smartphone, Code, Palette, Zap, Shield, Globe } from "lucide-react";

export default function ServicesSection() {
  const services = [
    {
      icon: Smartphone,
      title: "Native iOS Development",
      description:
        "Custom iOS apps built with Swift and SwiftUI, optimized for performance and user experience.",
      features: [
        "Swift & SwiftUI",
        "Core Data",
        "App Store Optimization",
        "iOS Design Guidelines",
      ],
    },
    {
      icon: Code,
      title: "Native Android Development",
      description:
        "High-performance Android applications using Kotlin and Jetpack Compose.",
      features: [
        "Kotlin & Jetpack Compose",
        "Room Database",
        "Google Play Optimization",
        "Material Design",
      ],
    },
    {
      icon: Globe,
      title: "Cross-Platform Development",
      description:
        "Build once, deploy everywhere with React Native and Flutter solutions.",
      features: [
        "React Native",
        "Flutter",
        "Code Reusability",
        "Faster Time to Market",
      ],
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description:
        "Beautiful, intuitive designs that users love and business owners appreciate.",
      features: [
        "User Research",
        "Wireframing",
        "Prototyping",
        "Design Systems",
      ],
    },
    {
      icon: Zap,
      title: "App Optimization",
      description:
        "Performance tuning, bug fixes, and feature enhancements for existing applications.",
      features: [
        "Performance Analysis",
        "Bug Fixing",
        "Feature Updates",
        "Code Refactoring",
      ],
    },
    {
      icon: Shield,
      title: "App Maintenance",
      description:
        "Ongoing support, updates, and maintenance to keep your app running smoothly.",
      features: [
        "Regular Updates",
        "Bug Monitoring",
        "Security Patches",
        "24/7 Support",
      ],
    },
  ];

  return (
    <section id="services" className="py-20 bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-6">
            Our Mobile App Development Services
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto mb-12">
            From concept to launch, we provide comprehensive mobile app
            development services that help businesses thrive in the digital
            landscape.
          </p>

          {/* Services Image */}
          <div className="max-w-4xl mx-auto mb-16">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/mobile-app-dev-applanta.jpg"
                alt="Mobile App Development Services"
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden w-full h-64 bg-gradient-to-br from-primary-100 to-accent-100 items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary-800 mb-2">
                    Mobile App Development
                  </h3>
                  <p className="text-secondary-600">
                    Professional mobile app development services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-slide-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center mb-6">
                  <Icon className="w-8 h-8 text-primary-600" />
                </div>

                <h3 className="text-xl font-semibold text-secondary-900 mb-4">
                  {service.title}
                </h3>

                <p className="text-secondary-600 mb-6">{service.description}</p>

                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-center text-sm text-secondary-600"
                    >
                      <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Prices Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-4">
              Pricing Plans
            </h3>
            <p className="text-lg text-secondary-600">
              Choose the perfect plan for your app development needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: "Basic MVP",
                description:
                  "Perfect for simple apps with core features and essential functionality.",
                priceRange: "$5,000 - $15,000",
              },
              {
                title: "Advanced MVP",
                description:
                  "Ideal for medium complexity apps with advanced features and integrations.",
                priceRange: "$15,000 - $50,000",
              },
              {
                title: "Enterprise",
                description:
                  "Comprehensive solution for complex enterprise apps with custom features and premium support.",
                priceRange: "$50,000+",
              },
            ].map((plan, index) => (
              <div
                key={plan.title}
                className={`relative bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 animate-slide-up ${
                  index === 1
                    ? "border-2 border-primary-600 transform scale-105"
                    : ""
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-secondary-900 mb-4">
                    {plan.title}
                  </h4>
                  <p className="text-secondary-600 mb-6 min-h-[60px]">
                    {plan.description}
                  </p>
                  <div className="mb-6">
                    <span className="text-3xl font-bold text-primary-600">
                      {plan.priceRange}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-secondary-900 mb-4">
              Our Development Process
            </h3>
            <p className="text-lg text-secondary-600">
              A proven methodology that ensures quality and efficiency
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Discovery",
                description: "Understanding your vision and requirements",
              },
              {
                step: "02",
                title: "Design",
                description: "Creating wireframes and visual designs",
              },
              {
                step: "03",
                title: "Development",
                description: "Building your app with clean, scalable code",
              },
              {
                step: "04",
                title: "Launch",
                description: "Testing, optimization, and app store deployment",
              },
            ].map((process, index) => (
              <div
                key={process.step}
                className="text-center animate-slide-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h4 className="text-lg font-semibold text-secondary-900 mb-2">
                  {process.title}
                </h4>
                <p className="text-secondary-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Development Process Image */}
        <div className="mt-20">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/mobile-app-dev-applanta-2.jpg"
                alt="Our Development Process"
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden w-full h-64 bg-gradient-to-br from-primary-100 to-accent-100 items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Code className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary-800 mb-2">
                    Development Process
                  </h3>
                  <p className="text-secondary-600">From discovery to launch</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
